import type { ResultRow } from './results';
import type { SessionView } from './sessions';

/**
 * What the Today screen works out from the centre's own lists: who is waiting
 * to be marked, what is coming up, what has just come in. Pure functions, so
 * the screen is only arrangement and the arithmetic has tests.
 */

/** The greeting a colleague would give at that hour. */
export function greeting(firstName: string, now: Date = new Date()): string {
  const hour = now.getHours();
  if (hour < 5) return `Working late, ${firstName}`;
  if (hour < 12) return `Good morning, ${firstName}`;
  if (hour < 17) return `Good afternoon, ${firstName}`;
  if (hour < 22) return `Good evening, ${firstName}`;
  return `Working late, ${firstName}`;
}

/** The name a person goes by: the first word of their full name. */
export const firstName = (fullName: string): string => fullName.trim().split(/\s+/)[0] ?? fullName;

/** "just now", "12 min ago", "3 h ago" — then the calendar takes over. */
export function sinceLabel(iso: string, now: Date = new Date()): string {
  const minutes = Math.floor((now.getTime() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const sameDay = new Date(iso).toDateString() === now.toDateString();
  if (sameDay) return `${Math.floor(minutes / 60)} h ago`;
  const days = Math.round(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
      new Date(new Date(iso).getFullYear(), new Date(iso).getMonth(), new Date(iso).getDate()).getTime()) /
      86_400_000,
  );
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/** Writing handed in and not yet read — the longest-waiting first. */
export const waitingForMarks = (rows: ResultRow[]): ResultRow[] =>
  rows
    .filter((row) => row.awaitingMarking)
    .sort((a, b) => a.gradedAt.localeCompare(b.gradedAt));

/** The newest marked papers. */
export const justIn = (rows: ResultRow[], count = 6): ResultRow[] =>
  rows
    .filter((row) => !row.awaitingMarking)
    .sort((a, b) => b.gradedAt.localeCompare(a.gradedAt))
    .slice(0, count);

export interface Week {
  handedIn: number;
  averageBand: number | null;
  candidates: number;
}

/** The last seven days, in three numbers. */
export function lastSevenDays(rows: ResultRow[], now: Date = new Date()): Week {
  const since = now.getTime() - 7 * 86_400_000;
  const recent = rows.filter((row) => new Date(row.gradedAt).getTime() >= since);
  const bands = recent.map((row) => row.bandScore).filter((b): b is number => b !== null);
  const average = bands.length ? bands.reduce((sum, b) => sum + b, 0) / bands.length : null;
  return {
    handedIn: recent.length,
    // Rounded to the nearest half, the only steps a band comes in.
    averageBand: average === null ? null : Math.round(average * 2) / 2,
    candidates: new Set(recent.map((row) => row.student.id)).size,
  };
}

/** Test days still to come or under way, soonest first. */
export const comingUp = (sessions: SessionView[], now: Date = new Date(), count = 4): SessionView[] =>
  sessions
    .filter((s) => s.state !== 'CLOSED' && new Date(s.endsAt).getTime() > now.getTime())
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    .slice(0, count);
